import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  addMyFavoritePlace,
  ApiError,
  fetchMyFavoritePlaceIds,
  removeMyFavoritePlace,
} from '../lib/api';

const STORAGE_KEY = 'ahg.favorites.places';

function readFavorites(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeFavorites(ids: string[]) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // ignore storage errors (quota, private mode, etc.)
  }
}

const EVENT_NAME = 'ahg.favorites.changed';

function migratedSessionKey(userId: string) {
  return `ahg.favorites.migrated.${userId}`;
}

export function useFavorites() {
  const { user, loading: authLoading } = useAuth();
  const [favorites, setFavorites] = useState<string[]>(() => readFavorites());

  useEffect(() => {
    const handleChange = () => setFavorites(readFavorites());
    window.addEventListener(EVENT_NAME, handleChange);
    window.addEventListener('storage', handleChange);
    return () => {
      window.removeEventListener(EVENT_NAME, handleChange);
      window.removeEventListener('storage', handleChange);
    };
  }, []);

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      setFavorites(readFavorites());
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        let remote = await fetchMyFavoritePlaceIds();
        const local = readFavorites();
        const migKey = migratedSessionKey(user.id);
        const already = sessionStorage.getItem(migKey);
        if (!already && local.length > 0 && remote.length === 0) {
          for (const pid of local) {
            try {
              await addMyFavoritePlace(pid);
            } catch (e) {
              if (e instanceof ApiError && e.code === 'NOT_FOUND') continue;
              throw e;
            }
          }
          remote = await fetchMyFavoritePlaceIds();
          writeFavorites([]);
        }
        sessionStorage.setItem(migKey, '1');
        if (!cancelled) setFavorites(remote);
      } catch {
        if (!cancelled) setFavorites(readFavorites());
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id, authLoading]);

  const isFavorite = useCallback(
    (id: string) => favorites.includes(id),
    [favorites],
  );

  const toggleFavorite = useCallback(
    async (id: string) => {
      if (user) {
        const was = favorites.includes(id);
        try {
          if (was) await removeMyFavoritePlace(id);
          else await addMyFavoritePlace(id);
          setFavorites((prev) =>
            was ? prev.filter((x) => x !== id) : [...prev, id],
          );
        } catch {
          try {
            setFavorites(await fetchMyFavoritePlaceIds());
          } catch {
            /* ignore */
          }
        }
      } else {
        const current = readFavorites();
        const next = current.includes(id)
          ? current.filter((x) => x !== id)
          : [...current, id];
        writeFavorites(next);
        setFavorites(next);
        window.dispatchEvent(new Event(EVENT_NAME));
      }
    },
    [user, favorites],
  );

  const clearFavorites = useCallback(() => {
    writeFavorites([]);
    setFavorites([]);
    window.dispatchEvent(new Event(EVENT_NAME));
  }, []);

  return {
    favorites,
    favoriteCount: favorites.length,
    isFavorite,
    toggleFavorite,
    clearFavorites,
  };
}
